import { LogOut, CheckSquare, FolderKanban, Users, CircleUser as UserCircle, Building2, User, LayoutDashboard, Settings, MoreHorizontal, StickyNote, Menu as MenuIcon, X, ChevronDown, FileText, UserCog, Wand2, Crown, Bot, MessageSquareCode, Code, Shield, Sparkles, Search, Bell, Home, Briefcase, ClipboardList, Building, Moon, Sun, PanelLeft, PanelRight, LayoutGrid, LayoutList, FileOutput, Image, CreditCard, ShieldAlert, Calendar, Share2 } from 'lucide-react';
import { Database } from '../types/supabase';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { refreshSession } from '../lib/auth';
import Logo from './Logo';
import { useDarkMode } from '../hooks/useDarkMode';
import { toast } from '../lib/toast';

type Task = Database['public']['Tables']['tasks']['Row'];
type Profile = Database['public']['Tables']['profiles']['Row'];

interface NavigationProps {
  onSignOut: () => void;
  tasks: Task[];
  currentView: string;
  onViewChange: (view: string) => void;
  navigate: (to: string) => void;
}

function Navigation({ onSignOut, tasks, currentView, onViewChange, navigate }: NavigationProps) {
  const [isDark, setIsDark] = useDarkMode();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      let retries = 0;
      const maxRetries = 3;
      let delay = 1000;

      while (retries < maxRetries) {
        try {
          const session = await refreshSession();
          if (!session?.user) {
            setProfile(null);
            return;
          }

          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (error) throw error;
          if (data) {
            setProfile(data);
            
            // Check if user is super admin
            const { data: role } = await supabase
              .from('roles')
              .select('name')
              .eq('id', data.role_id)
              .single();
              
            if (role && role.name === 'super_admin') {
              setIsSuperAdmin(true);
            }
            return;
          }
        } catch (error) {
          retries++;
          if (retries === maxRetries) throw error;
          await new Promise(resolve => setTimeout(resolve, delay));
          delay = Math.min(delay * 2, 5000);
        }
      }

      throw new Error('Failed to fetch profile after retries');
    } catch (error) {
      console.error('Error fetching profile:', error);
      const message = error instanceof Error 
        ? error.message 
        : 'Failed to load profile. Please try refreshing the page.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    setShowProfileMenu(false);
    onSignOut();
  };

  return (
    <>
      {/* Vertical Sidebar */}
      <div className={`fixed left-0 top-0 h-screen w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 z-40 flex flex-col ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700">
          <Logo onClick={() => {
            if (profile?.id) {
              navigate('/overview');
            } else {
              navigate('/');
            }
            setIsMobileMenuOpen(false);
          }} />
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500"
          >
            <X className="block h-5 w-5" />
          </button>
        </div>

        {/* Navigation Items - Scrollable */}
        <div className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            <button
              onClick={() => {
                navigate('/');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                window.location.pathname === '/'
                  ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <LayoutDashboard className="h-5 w-5 mr-3" />
              Dashboard
            </button>
            <button
              onClick={() => {
                navigate('/customers');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                window.location.pathname === '/customers'
                  ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Users className="h-5 w-5 mr-3" />
              Customers
            </button>
            <button
              onClick={() => {
                navigate('/projects');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                window.location.pathname === '/projects'
                  ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <FolderKanban className="h-5 w-5 mr-3" />
              Projects
            </button>
            <button
              onClick={() => {
                navigate('/tasks');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                window.location.pathname === '/tasks'
                  ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <CheckSquare className="h-5 w-5 mr-3" />
              Tasks
            </button>
            <button
              onClick={() => {
                navigate('/employees');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                window.location.pathname === '/employees'
                  ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Users className="h-5 w-5 mr-3" />
              My Team
            </button>
            <button
              onClick={() => {
                navigate('/research');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                window.location.pathname === '/research'
                  ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Wand2 className="h-5 w-5 mr-3" />
              Research
            </button>
            <button
              onClick={() => {
                navigate('/ai-agents');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                window.location.pathname === '/ai-agents'
                  ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <Bot className="h-5 w-5 mr-3" />
              AI Agents
            </button>

            <div className="py-2 border-t border-gray-200 dark:border-gray-700 mt-2">
              <button
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                <span>More</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isMoreOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMoreOpen && (
                <>
                  <button
                    onClick={() => {
                      navigate('/agent-outputs');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                      window.location.pathname === '/agent-outputs'
                        ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Image className="h-5 w-5 mr-3" />
                    Agent Outputs
                  </button>
                  <button
                    onClick={() => {
                      navigate('/notes');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                      window.location.pathname === '/notes'
                        ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <StickyNote className="h-5 w-5 mr-3" />
                    Notes
                  </button>
                  <button
                    onClick={() => {
                      navigate('/prompts');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                      window.location.pathname === '/prompts'
                        ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <MessageSquareCode className="h-5 w-5 mr-3" />
                    Prompts
                  </button>
                  <button
                    onClick={() => {
                      navigate('/reports');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                      window.location.pathname === '/reports'
                        ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FileText className="h-5 w-5 mr-3" />
                    AI Reports
                  </button>
                  <button
                    onClick={() => {
                      navigate('/daily-planner');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                      window.location.pathname === '/daily-planner'
                        ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Calendar className="h-5 w-5 mr-3" />
                    Daily Planner
                  </button>
                  <button
                    onClick={() => {
                      navigate('/workspace-invite');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                      window.location.pathname === '/workspace-invite'
                        ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Share2 className="h-5 w-5 mr-3" />
                    Workspace Invite
                  </button>
                  {userRole === 'super_admin' && (
                    <button
                      onClick={() => {
                        navigate('/user-accounts');
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                        window.location.pathname === '/user-accounts'
                          ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <UserCog className="h-5 w-5 mr-3" />
                      User Accounts
                    </button>
                  )}
                  {isSuperAdmin && (
                    <button
                      onClick={() => {
                        navigate('/super-admin');
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                        window.location.pathname === '/super-admin'
                          ? 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <ShieldAlert className="h-5 w-5 mr-3" />
                      Super Admin
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - Account Menu */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-1">
          {!isLoading && !profile?.id && (
            <a
              href="/auth"
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Sign In
            </a>
          )}
          {profile?.id && (
            <div className="relative">
              <button
                onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex-1 text-left">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">My Account</p>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showAccountDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showAccountDropdown && (
                <div className="mt-1 rounded-md bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 shadow-lg overflow-hidden">
                  <button
                    onClick={() => {
                      navigate('/my-account');
                      setIsMobileMenuOpen(false);
                      setShowAccountDropdown(false);
                    }}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
                  >
                    <User className="h-5 w-5 mr-3" />
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      navigate('/my-account');
                      setIsMobileMenuOpen(false);
                      setShowAccountDropdown(false);
                    }}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left border-t border-gray-200 dark:border-gray-600"
                  >
                    <Bell className="h-5 w-5 mr-3" />
                    Invitations
                  </button>
                  <button
                    onClick={() => {
                      navigate('/my-account');
                      setIsMobileMenuOpen(false);
                      setShowAccountDropdown(false);
                    }}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left border-t border-gray-200 dark:border-gray-600"
                  >
                    <Users className="h-5 w-5 mr-3" />
                    Membership
                  </button>
                  <button
                    onClick={() => {
                      navigate('/my-account');
                      setIsMobileMenuOpen(false);
                      setShowAccountDropdown(false);
                    }}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left border-t border-gray-200 dark:border-gray-600"
                  >
                    <CircleUser className="h-5 w-5 mr-3" />
                    Help
                  </button>
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setIsMobileMenuOpen(false);
                      setShowAccountDropdown(false);
                    }}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left border-t border-gray-200 dark:border-gray-600"
                  >
                    <Settings className="h-5 w-5 mr-3" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setShowAccountDropdown(false);
                    }}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left border-t border-gray-200 dark:border-gray-600"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    Log out
                  </button>
                  {profile && (
                    <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{profile.full_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{profile.email}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main content wrapper */}
      <div className="lg:ml-64">
        {/* Top header bar */}
        <nav className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500"
              >
                {isMobileMenuOpen ? (
                  <X className="block h-6 w-6" />
                ) : (
                  <MenuIcon className="block h-6 w-6" />
                )}
              </button>

              <div className="flex-1"></div>
            </div>
          </div>
        </nav>
      </div>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>

  );
}

export default Navigation;